import { Alert, Button, Card, Field, Input, PasswordInput } from "@cafe/ui";
import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { auth, errorText, http, useSession } from "../lib/api";
import { CocaLogo } from "../logo";

type Step = { status: "setup_2fa" | "2fa_required"; challenge: string; secret?: string; otpauthUrl?: string };

/** Вход сотрудника платформы: пароль → обязательный TOTP (при первом входе — привязка приложения-аутентификатора). */
export default function LoginPage() {
  const session = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<Step | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    setQr(null);
    if (step?.status !== "setup_2fa" || !step.otpauthUrl) return;
    let alive = true;
    void QRCode.toDataURL(step.otpauthUrl, { margin: 1, width: 240 }).then((url) => alive && setQr(url));
    return () => { alive = false; };
  }, [step]);
  if (session) return <Navigate to="/" replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (!step) setStep(await http.post<Step>("/auth/login", { email, password }));
      else {
        const r = await http.post<{ accessToken: string; expiresAt: string; user: { id: string; name: string; email: string; role: string } }>("/auth/2fa/verify", { challenge: step.challenge, code });
        auth.set({ token: r.accessToken, user: r.user, expiresAt: r.expiresAt });
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-dvh place-items-center bg-bg p-4">
      <Card className="flex w-full max-w-md flex-col gap-5 p-6">
        <div>
          <h1 className="flex items-end gap-2"><CocaLogo className="h-10 w-auto" /><span className="pb-1 text-lg font-semibold text-muted">Platform</span></h1>
          <p className="mt-1 text-sm text-muted">{step ? "Подтверждение входа" : "Вход для сотрудников платформы"}</p>
        </div>
        {error && <Alert tone="danger">{error}</Alert>}
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-5">
          {!step ? (
            <>
              <Field label="Email" required><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" autoFocus /></Field>
              <Field label="Пароль" required><PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" /></Field>
              <Link to="/forgot-password" className="self-start text-sm font-semibold text-primary hover:underline">Забыли пароль?</Link>
            </>
          ) : (
            <>
              {step.status === "setup_2fa" && (
                <>
                  <Alert tone="info" title="Защитите вход кодом с телефона">
                    Установите на телефон приложение для кодов (Google Authenticator, 1Password и т. п.) и отсканируйте QR-код. Если камера недоступна — введите ключ вручную: <code className="break-all font-semibold">{step.secret}</code>
                  </Alert>
                  {qr && <img src={qr} alt="QR-код для приложения-аутентификатора" width={240} height={240} className="mx-auto rounded-tile border border-border" />}
                </>
              )}
              <Field label="Код из приложения на телефоне" required hint="6 цифр, меняется каждые 30 секунд">
                <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" autoFocus />
              </Field>
            </>
          )}
          <Button type="submit" size="lg" loading={busy} disabled={step ? code.length !== 6 : !email || !password}>{step ? "Подтвердить" : "Войти"}</Button>
          {step && <Button variant="ghost" onClick={() => { setStep(null); setCode(""); }}>Назад</Button>}
        </form>
      </Card>
    </div>
  );
}
